/**
 * ImageConfigForm Component
 *
 * Main form for configuring image generation parameters.
 * Combines provider selector, prompt input, model selector, resolution selector,
 * style selector, and advanced settings.
 *
 * Provider-aware rendering:
 * - Fooocus: Full feature set (styles, models, advanced settings)
 * - Other providers: Simplified UI with only supported features
 */

import { useCallback, useMemo, useState } from 'react';
import { CreditCard, ImageIcon, Loader2 } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { PromptInput } from './PromptInput';
import { ResolutionSelector } from './ResolutionSelector';
import { StyleSelector } from './StyleSelector';
import { AdvancedSettings } from './AdvancedSettings';
import { ImageProviderSelector } from './ImageProviderSelector';
import { ImageModelSelector } from './ImageModelSelector';
import { TokenEstimateCard } from '../tokens/TokenEstimateCard';
import { useImageProviderAvailability } from '../../hooks/useImageProviderAvailability';
import { buildImagePricingFields } from '../../services/imageGenerationParams';
import type { ImageGenerationConfig, ImageModelInfo } from '../../types';
import {
  getProviderFeatures,
  FOOOCUS_ONLY_STYLES,
  IMAGE_GENERATION_OPERATION_LABEL_KEY,
  type AIImageProvider,
} from '../../constants/imageSource';
import { useTokenGate } from '../../hooks/useTokenGate';
import {
  COST_GATE_OPEN,
  costGateBlockLabel,
  type CostGate,
  type CostGateStatus,
} from '../../utils/costGate';

interface ImageConfigFormProps {
  config: ImageGenerationConfig;
  onConfigChange: React.Dispatch<React.SetStateAction<ImageGenerationConfig>>;
  onSubmit: () => void;
  isLoading?: boolean;
  error?: string | null;
  /**
   * The live quote from the cost card. Lifted out of this component because the
   * page needs the SAME number when the server later refuses the request for
   * lack of tokens (HTTP 402) — the purchase modal shows "you need N more", and
   * N is this quote, not something re-derived or parsed out of an error string.
   */
  onEstimateChange?: (gate: CostGate) => void;
}

export function ImageConfigForm({
  config,
  onConfigChange,
  onSubmit,
  isLoading = false,
  error,
  onEstimateChange,
}: ImageConfigFormProps) {
  const { t } = useTranslation();
  const { reportInsufficientTokens } = useTokenGate();
  const [costGate, setCostGate] = useState<CostGateStatus>(COST_GATE_OPEN);
  const [estimatedTokens, setEstimatedTokens] = useState(0);
  // Per-model generation recommendations (e.g. Hyper checkpoints converge in
  // ~6 steps at low CFG). Surfaced as a hint next to the Steps slider.
  const [modelRecommendation, setModelRecommendation] = useState<{
    steps?: number;
    cfg?: number;
  } | null>(null);

  const imageOperationName = t(IMAGE_GENERATION_OPERATION_LABEL_KEY, {
    defaultValue: 'AI Image Generation',
  });

  // Get feature flags for current provider
  const providerFeatures = useMemo(
    () => getProviderFeatures(config.provider as AIImageProvider),
    [config.provider]
  );

  // Real, per-provider install state (same source of truth as ImageProviderSelector's
  // "Free" badge — GET /api/v1/video-providers/image-availability). A hybrid
  // provider (Fooocus -> fooocus_runpod) is only actually free when its local
  // component is installed; without this, the estimate/cost card treated the
  // provider's mere CAPABILITY to run locally as proof it was running locally,
  // showing "Cost: Free" for a generation that RunPod billed for real.
  const { availability: imageAvailability } = useImageProviderAvailability();
  const currentProviderAvailability = imageAvailability?.get(config.provider) ?? null;
  const isLocalProviderNow: boolean =
    currentProviderAvailability !== null &&
    currentProviderAvailability.is_local &&
    currentProviderAvailability.is_installed;

  // Token estimate parameters — POST /api/v1/image/generate's OWN field names,
  // the ones GET /api/v1/cost/operations publishes for it, built by the SAME
  // function the Generate button builds its request with
  // (services/imageGenerationParams.ts). They used to be built here by hand and
  // again in imageGenerationAPI.generateImage, and the two hands disagreed
  // about num_steps: this card forwarded the 0 that the six OpenRouter
  // providers carry (steps do not apply to them) and got a 400 instead of a
  // price, while the submit path quietly turned the same 0 into 30.
  const { resolution, width, height, num_steps, model, num_images, provider } = config;
  const estimateParams = useMemo(
    () => ({
      ...buildImagePricingFields({
        resolution,
        width,
        height,
        num_steps,
        model,
        num_images,
        provider,
      }),
      // Overrides the backend's capability-only default — see
      // shared/billing/estimators/image_estimator.py
      // (_estimate_image_generation `installed` parameter).
      installed: isLocalProviderNow,
    }),
    [resolution, width, height, num_steps, model, num_images, provider, isLocalProviderNow]
  );

  // Handle token estimate change
  const handleEstimateChange = useCallback(
    (gate: CostGate) => {
      setCostGate(gate.status);
      setEstimatedTokens(gate.tokens);
      onEstimateChange?.(gate);
    },
    [onEstimateChange]
  );

  const handleFieldChange = useCallback(
    <K extends keyof ImageGenerationConfig>(field: K, value: ImageGenerationConfig[K]) => {
      onConfigChange((prev) => ({ ...prev, [field]: value }));
    },
    [onConfigChange]
  );

  // Handle provider change - reset provider-specific fields
  const handleProviderChange = useCallback(
    (provider: AIImageProvider) => {
      const features = getProviderFeatures(provider);
      // Different providers expose different checkpoint catalogs, so a model id
      // from the previous provider is never valid for the new one. Always reset
      // to undefined and let ImageModelSelector auto-select the new provider's
      // default (which re-applies that model's recommended steps / CFG).
      setModelRecommendation(null);
      onConfigChange((prev) => ({
        ...prev,
        provider,
        model: undefined,
        // Filter out Fooocus-only styles when switching to other providers
        styles: features.supportsStyles
          ? provider === 'fooocus'
            ? prev.styles
            : prev.styles.filter(
                (s) => !FOOOCUS_ONLY_STYLES.includes(s as (typeof FOOOCUS_ONLY_STYLES)[number])
              )
          : [],
        // Reset steps and guidance to provider defaults
        num_steps: features.defaultNumSteps,
        guidance_scale: features.defaultGuidance,
      }));
    },
    [onConfigChange]
  );

  const handleResolutionChange = useCallback(
    (preset: string, width?: number, height?: number) => {
      onConfigChange((prev) => ({
        ...prev,
        resolution: preset === 'custom' ? `${width}x${height}` : preset,
        width: preset === 'custom' ? width : undefined,
        height: preset === 'custom' ? height : undefined,
      }));
    },
    [onConfigChange]
  );

  const handleModelChange = useCallback(
    (model: string, meta?: ImageModelInfo) => {
      onConfigChange((prev) => {
        const next = { ...prev, model };
        // Apply the checkpoint's recommended generation params. Critical for
        // Hyper models: leaving CFG at the standard 7.0 burns the image, and
        // the standard ~25 steps wastes time for no quality gain.
        if (typeof meta?.recommended_steps === 'number') {
          next.num_steps = meta.recommended_steps;
        }
        if (typeof meta?.recommended_cfg === 'number') {
          next.guidance_scale = meta.recommended_cfg;
        }
        return next;
      });
      setModelRecommendation(
        meta && (meta.recommended_steps != null || meta.recommended_cfg != null)
          ? { steps: meta.recommended_steps, cfg: meta.recommended_cfg }
          : null
      );
    },
    [onConfigChange]
  );

  // A balance short of a real quote offers a purchase (handleSubmit); a quote
  // that could not be produced blocks the action outright -- there is nothing
  // to buy against an unknown price (src/utils/costGate.ts).
  const balanceShort = costGate === 'insufficient';
  const estimateUnavailable = costGate === 'unavailable';

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!config.prompt.trim()) return;
      // Short on tokens: open the purchase modal instead of submitting. The
      // button used to be DISABLED here, which stated the problem ("You need N
      // more tokens") and offered no way to solve it — a dead end on the one
      // screen where the fix is a single click. The server would refuse the
      // request anyway (402), so nothing is lost by not sending it.
      if (balanceShort) {
        reportInsufficientTokens(estimatedTokens, imageOperationName);
        return;
      }
      onSubmit();
    },
    [
      config.prompt,
      onSubmit,
      balanceShort,
      estimatedTokens,
      reportInsufficientTokens,
      imageOperationName,
    ]
  );

  // The prompt still gates submission, and so does a cost card that could not
  // produce a quote. A balance too low no longer does — see handleSubmit, which
  // turns that into an offer to buy.
  const isValid = config.prompt.trim().length > 0 && !estimateUnavailable;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Provider Selector - Always visible */}
      <ImageProviderSelector
        value={config.provider as AIImageProvider}
        onChange={handleProviderChange}
        disabled={isLoading}
      />

      {/* Model Selector - Providers that support model choice (Fooocus SDXL, ComfyUI SD 1.5) */}
      {providerFeatures.supportsModels && (
        <ImageModelSelector
          value={config.model}
          onChange={handleModelChange}
          provider={config.provider}
          disabled={isLoading}
        />
      )}

      {/* Prompt Section - Show negative prompt only for supporting providers */}
      <PromptInput
        prompt={config.prompt}
        negativePrompt={providerFeatures.supportsNegativePrompt ? config.negative_prompt : ''}
        onPromptChange={(value) => handleFieldChange('prompt', value)}
        onNegativePromptChange={(value) => handleFieldChange('negative_prompt', value)}
        disabled={isLoading}
        showNegativePrompt={providerFeatures.supportsNegativePrompt}
      />

      {/* Resolution Section */}
      <ResolutionSelector
        value={config.resolution}
        customWidth={config.width}
        customHeight={config.height}
        onChange={handleResolutionChange}
        disabled={isLoading}
        provider={config.provider}
      />

      {/* Style Section - Available for all providers that support styles */}
      {providerFeatures.supportsStyles && (
        <StyleSelector
          selectedStyles={config.styles}
          onChange={(styles) => handleFieldChange('styles', styles)}
          maxStyles={10}
          disabled={isLoading}
          provider={config.provider}
        />
      )}

      {/* Advanced Settings - Show only supported features */}
      <AdvancedSettings
        numImages={config.num_images}
        seed={config.seed}
        guidanceScale={config.guidance_scale}
        numSteps={config.num_steps}
        sharpness={config.sharpness}
        performance={config.performance}
        outputFormat={config.output_format}
        onNumImagesChange={(value) => handleFieldChange('num_images', value)}
        onSeedChange={(value) => handleFieldChange('seed', value)}
        onGuidanceScaleChange={(value) => handleFieldChange('guidance_scale', value)}
        onNumStepsChange={(value) => handleFieldChange('num_steps', value)}
        onSharpnessChange={(value) => handleFieldChange('sharpness', value)}
        onPerformanceChange={(value) => handleFieldChange('performance', value)}
        onOutputFormatChange={(value) => handleFieldChange('output_format', value)}
        disabled={isLoading}
        // Provider feature flags
        showSeed={providerFeatures.supportsSeed}
        showNumSteps={providerFeatures.supportsNumSteps}
        showGuidanceScale={providerFeatures.supportsGuidance}
        showSharpness={providerFeatures.supportsSharpness}
        showPerformance={providerFeatures.supportsPerformance}
        showOutputFormat={providerFeatures.supportsOutputFormat}
        maxNumSteps={providerFeatures.maxNumSteps}
        maxGuidanceScale={providerFeatures.maxGuidance}
        // Per-model recommendations (e.g. Hyper checkpoints)
        recommendedSteps={modelRecommendation?.steps}
        recommendedGuidanceScale={modelRecommendation?.cfg}
      />

      {/* Token Estimate */}
      <TokenEstimateCard
        operationPath="/api/v1/image/generate"
        parameters={estimateParams}
        onEstimateChange={handleEstimateChange}
        isLocalProvider={isLocalProviderNow}
      />

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isLoading || !isValid}
          className="btn-primary px-8 py-3 text-base font-semibold flex items-center gap-2"
          data-testid="generate-image-button"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              {t('image.generating', { defaultValue: 'Generating...' })}
            </>
          ) : balanceShort ? (
            /* Says what will happen on click: the purchase modal, not a
               generation. The same wording every other paid tab uses. */
            <>
              <CreditCard className="h-5 w-5" />
              {t('tokens.buyTokens', { defaultValue: 'Buy Tokens' })}
            </>
          ) : estimateUnavailable ? (
            <>
              <ImageIcon className="h-5 w-5" />
              {costGateBlockLabel(costGate, t)}
            </>
          ) : (
            <>
              <ImageIcon className="h-5 w-5" />
              {t('image.generate', { defaultValue: 'Generate Image' })}
            </>
          )}
        </button>
      </div>
    </form>
  );
}

export default ImageConfigForm;
